// apps/web/src/hooks/useVersionCheck.ts

import { useEffect, useState, useCallback, useRef } from 'react';
import type { VersionMetadata } from '@/lib/types/version.types';
import {
  checkForUpdate,
  initializeVersion,
  forceRefresh,
} from '@/lib/version-check';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface UseVersionCheckOptions {
  /** Check interval in milliseconds (default: 5 minutes) */
  intervalMs?: number;
  /** Disable in development mode (default: true) */
  disableInDev?: boolean;
}

interface UseVersionCheckReturn {
  /** Whether a new version is available */
  updateAvailable: boolean;
  /** New version metadata if available */
  newVersion: VersionMetadata | null;
  /** Dismiss the update notification */
  dismissUpdate: () => void;
  /** Force refresh to load new version */
  refresh: () => Promise<void>;
  /** Whether currently checking for updates */
  isChecking: boolean;
}

/**
 * Hook to check for app version updates.
 * Polls /version.json every 5 minutes and notifies when new version detected.
 */
export function useVersionCheck(
  options: UseVersionCheckOptions = {}
): UseVersionCheckReturn {
  const { intervalMs = CHECK_INTERVAL_MS, disableInDev = true } = options;

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState<VersionMetadata | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef(false);

  const isDevelopment = import.meta.env.DEV;

  const performCheck = useCallback(async () => {
    // Use ref to prevent unnecessary re-creation of this callback
    if (isCheckingRef.current) return;

    isCheckingRef.current = true;
    setIsChecking(true);
    try {
      const result = await checkForUpdate();
      if (result.hasUpdate && result.newVersion) {
        console.warn('[useVersionCheck] New version detected:', result.newVersion.version);
        setUpdateAvailable(true);
        setNewVersion(result.newVersion);
      }
    } catch (error) {
      console.warn('[useVersionCheck] Check failed:', error);
    } finally {
      isCheckingRef.current = false;
      setIsChecking(false);
    }
  }, []);

  const dismissUpdate = useCallback(() => {
    setUpdateAvailable(false);
    // Note: Will re-appear on next check if still different
  }, []);

  const refresh = useCallback(async () => {
    await forceRefresh();
  }, []);

  useEffect(() => {
    // Skip in development mode (HMR handles updates)
    if (disableInDev && isDevelopment) {
      console.warn('[useVersionCheck] Disabled in development mode');
      return;
    }

    // Initialize version and perform immediate check
    void initializeVersion().then(() => {
      void performCheck();
    });

    // Start periodic checking
    intervalRef.current = setInterval(() => {
      void performCheck();
    }, intervalMs);

    // Also check on visibility change (when user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void performCheck();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [intervalMs, disableInDev, isDevelopment, performCheck]);

  return {
    updateAvailable,
    newVersion,
    dismissUpdate,
    refresh,
    isChecking,
  };
}
